import React, { useEffect } from 'react'
import { useFormik } from 'formik'
import { getUserId } from '../../helpers/auth'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { Button, DatePicker, Input, Modal } from 'antd'
import Loader from '../../pages/pages/loader'
import CustomersDropdown from '../CustomersDropdown'
import AppStoreDropdown from '../AppStoreDropdown'
import AppsDropdown from '../AppsDropdown'
import dayjs from 'dayjs'
import AppStatusDropdown from '../AppStatusDropdown'
import {
  useCreateAppStoreDataMutation,
  useUpdateAppStoreDataMutation
} from '../../redux/apis/linkManagement/app/data/lmAppsDataApiSlice'
import TextArea from 'antd/es/input/TextArea'
import AssigneeDropdown from '../AssigneeDropdown'

const EditCreateAppData = ({ state, open, setOpen, record }) => {
  const [
    updateService,
    { isLoading: isUpdating, isSuccess: SuccessUpdate, error: errorUpdate }
  ] = useUpdateAppStoreDataMutation()
  const [
    addService,
    { isLoading: isAdding, isSuccess: SuccessAdd, error: errorAdd }
  ] = useCreateAppStoreDataMutation()

  const today = new Date().toISOString().split('T')[0]
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      url: record?.url || '',
      crawl_date: state === 'edit' ? record?.crawl_date : today,
      review_date: state === 'edit' ? record?.review_date : today,
      report_date: record?.report_date || '',
      remove_date: record?.remove_date || '',
      report_case_id: record?.report_case_id || '',
      installation_count: record?.installation_count || '',
      extra_info: record?.extra_info || '',
      app: record?.app?.id || '',
      app_store: record?.app_store?.id || '',
      status: state === 'edit' ? record?.status?.id : 1,
      customers: record?.customers?.map(x => x.customer),
      last_updated_by: getUserId(),
      found_by: record?.found_by?.id || ''
    },

    validationSchema: Yup.object({
      url: Yup.string().required('Please Enter Url'),
      crawl_date: Yup.string().required('Please Enter Crawl date'),
      installation_count: Yup.string().required(
        'Please Enter Installation count'
      ),
      app: Yup.number().required('Please Enter App '),
      app_store: Yup.number().required('Please Enter App Store'),
      status: Yup.number().required('Please Enter Status'),
      customers: Yup.array().required('Please Enter at least one Customer')
    }),

    onSubmit: async values => {
      if(values.report_date.length === 0){
        delete values.report_date
      }
      if(values.remove_date.length === 0){
        delete values.remove_date
      }
      try {
        if (state === 'edit') {
          await updateService({
            body: values, // plain JSON object
            id: record?.id
          }).unwrap()
        } else if (state === 'create') {
          await addService(values).unwrap()
        }
      } catch (err) {
        console.error(err)
      }
    }
  })

  useEffect(() => {
    if (errorAdd?.data) {
      const errorMessages = Object.values(errorAdd.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    } else if (errorUpdate?.data) {
      const errorMessages = Object.values(errorUpdate.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [errorAdd, errorUpdate])

  useEffect(() => {
    if (SuccessAdd && state === 'create') {
      toast.success('App Data created successfully!')
      setOpen(false)
    } else if (SuccessUpdate && state === 'edit') {
      toast.success('App Data successfully!')
      setOpen(false)
    }
  }, [state, SuccessAdd, SuccessUpdate])

  return (
    <Modal
      className='customModal'
      open={open}
      title={state === 'edit' ? `Edit App Data` : 'Create App Data'}
      onCancel={() => setOpen(false)}
      footer={[
        <Button key='back' onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          onClick={() => {
            if (Object.keys(validation.errors).length !== 0) {
              Object.keys(validation.errors).map(key => {
                toast.error(validation.errors[key])
              })
              return
            }
            validation.handleSubmit()
          }}
        >
          Submit
        </Button>
      ]}
    >
      <Loader isLoading={isAdding || isUpdating} />
      <div className=' mb-3'>
        <CustomersDropdown
          onChange={e => {
            validation.setFieldValue('customers', e)
          }}
          value={validation.values.customers}
          onBlur={validation.handleBlur}
          mode='multiple'
        />
        {validation.touched.customers && validation.errors.customers ? (
          <p className='text-danger'>{validation.errors.customers}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <AppStoreDropdown
          onChange={e => {
            validation.setFieldValue('app_store', e)
          }}
          value={validation.values.app_store}
          onBlur={validation.handleBlur}
        />
        {validation.touched.app_store && validation.errors.app_store ? (
          <p className='text-danger'>{validation.errors.app_store}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <AppsDropdown
          onChange={e => {
            validation.setFieldValue('app', e)
          }}
          value={validation.values.app}
          onBlur={validation.handleBlur}
        />
        {validation.touched.app && validation.errors.app ? (
          <p className='text-danger'>{validation.errors.app}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">URL</label>
        <Input
          type='text'
          name='url'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.url}
        />
        {validation.touched.url && validation.errors.url ? (
          <p className='text-danger'>{validation.errors.url}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">Crawl Date</label>
        <br />
        <DatePicker
          style={{ width: '100%' }}
          name='crawl_date'
          onChange={(e, date) => validation.setFieldValue('crawl_date', date)}
          onBlur={validation.handleBlur}
          value={
            validation.values.crawl_date
              ? dayjs(validation.values.crawl_date)
              : ''
          }
        />
        {validation.touched.crawl_date && validation.errors.crawl_date ? (
          <p className='text-danger'>{validation.errors.crawl_date}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">Report Date</label>
        <br />
        <DatePicker
          style={{ width: '100%' }}
          name='report_date'
          onChange={(e, date) => validation.setFieldValue('report_date', date)}
          onBlur={validation.handleBlur}
          value={
            validation.values.report_date
              ? dayjs(validation.values.report_date)
              : ''
          }
        />
        {validation.touched.report_date && validation.errors.report_date ? (
          <p className='text-danger'>{validation.errors.report_date}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">Remove Date</label>
        <br />
        <DatePicker
          style={{ width: '100%' }}
          name='remove_date'
          onChange={(e, date) => validation.setFieldValue('remove_date', date)}
          onBlur={validation.handleBlur}
          value={
            validation.values.remove_date
              ? dayjs(validation.values.remove_date)
              : ''
          }
        />
        {validation.touched.remove_date && validation.errors.remove_date ? (
          <p className='text-danger'>{validation.errors.remove_date}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">Report Case ID</label>
        <Input
          type='text'
          name='report_case_id'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.report_case_id}
        />
        {validation.touched.report_case_id &&
        validation.errors.report_case_id ? (
          <p className='text-danger'>{validation.errors.report_case_id}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">Installation Count</label>
        <Input
          type='text'
          name='installation_count'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.installation_count}
        />
        {validation.touched.installation_count &&
        validation.errors.installation_count ? (
          <p className='text-danger'>{validation.errors.installation_count}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">Extra Information</label>
        <TextArea
          type='text'
          name='extra_info'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.extra_info}
        />
        {validation.touched.extra_info && validation.errors.extra_info ? (
          <p className='text-danger'>{validation.errors.extra_info}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <AppStatusDropdown
          onChange={e => {
            validation.setFieldValue('status', e)
          }}
          value={validation.values.status}
          onBlur={validation.handleBlur}
        />
        {validation.touched.status && validation.errors.status ? (
          <p className='text-danger'>{validation.errors.status}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <AssigneeDropdown
          label='Found By'
          onChange={e => validation.setFieldValue('found_by', e)}
          value={validation.values.found_by || undefined}
          onBlur={validation.handleBlur}
        />
        {validation.touched.found_by && validation.errors.found_by ? (
          <p className='text-danger'>{validation.errors.found_by}</p>
        ) : null}
      </div>
    </Modal>
  )
}

export default EditCreateAppData
